/** Link targets shared by the public site's header, footer and home page. */

/** A menu entry: `to` is an app route, or a server-rendered /site page (plain link). */
export interface NavItem {
  label: string;
  to: string;
  hint?: string;
}

export const MENUS: { label: string; items: NavItem[] }[] = [
  {
    label: "Markets",
    items: [
      { label: "Share market today", to: "/share-market-today", hint: "Indices, breadth and movers" },
      { label: "Top gainers", to: "/top-gainers" },
      { label: "Top losers", to: "/top-losers" },
      { label: "Most active", to: "/most-active" },
      { label: "52-week high", to: "/52-week-high" },
      { label: "Indices", to: "/indices" },
    ],
  },
  {
    label: "Research",
    items: [
      { label: "Stocks", to: "/stocks", hint: "NIFTY 500 share prices" },
      { label: "Sectors", to: "/sectors" },
      { label: "Stock screener", to: "/pricing", hint: "Filter NSE shares your way" },
      { label: "Option chain and F&O", to: "/pricing", hint: "OI, IV, PCR and max pain" },
    ],
  },
  {
    // Free public directories, and joining as a Research Analyst (V063).
    label: "Directory",
    items: [
      { label: "Find a Research Analyst", to: "/find-research-analyst", hint: "From SEBI's public register" },
      { label: "Research Analysts by city", to: "/research-analysts" },
      { label: "Find an Investment Adviser", to: "/find-investment-adviser" },
      { label: "Investment Advisers by city", to: "/investment-advisers" },
      { label: "For Research Analysts", to: "/for-research-analysts", hint: "Publish calls to your clients" },
    ],
  },
];

/** Plain links after the menus (React pages). */
export const TOP_LINKS: { label: string; to: "/services" | "/pricing" }[] = [
  { label: "Services", to: "/services" },
  { label: "Pricing", to: "/pricing" },
];

/** Site page for a NIFTY share: /stocks/<symbol> redirects to the canonical slug. */
export function stockHref(symbol: string): string {
  return `/stocks/${encodeURIComponent(symbol.toLowerCase())}`;
}

/** Ticker index symbols to their /indices page. */
export const INDEX_SLUGS: Record<string, string> = {
  NIFTY: "nifty-50",
  BANKNIFTY: "nifty-bank",
  SENSEX: "bse-sensex",
  FINNIFTY: "nifty-financial-services",
  BANKEX: "bse-bankex",
  MIDCPNIFTY: "nifty-midcap-select",
  "INDIA VIX": "india-vix",
};

/** Display names for the ticker's index symbols. */
export const INDEX_NAMES: Record<string, string> = {
  NIFTY: "NIFTY 50",
  BANKNIFTY: "BANK NIFTY",
  SENSEX: "SENSEX",
  FINNIFTY: "FIN NIFTY",
  BANKEX: "BANKEX",
  MIDCPNIFTY: "MIDCAP SELECT",
  "INDIA VIX": "INDIA VIX",
};

/** Cities with the most analysts on SEBI's register, and their /site pages. */
export const TOP_CITIES: [string, string][] = [
  ["Mumbai", "maharashtra/mumbai"],
  ["Indore", "madhya-pradesh/indore"],
  ["Bengaluru", "karnataka/bangalore"],
  ["Pune", "maharashtra/pune"],
  ["New Delhi", "delhi/new-delhi"],
  ["Kolkata", "west-bengal/kolkata"],
  ["Ahmedabad", "gujarat/ahmedabad"],
  ["Surat", "gujarat/surat"],
  ["Thane", "maharashtra/thane"],
  ["Hyderabad", "telangana/hyderabad"],
  ["Jaipur", "rajasthan/jaipur"],
  ["Chennai", "tamil-nadu/chennai"],
  ["Rajkot", "gujarat/rajkot"],
  ["Bhopal", "madhya-pradesh/bhopal"],
  ["Noida", "uttar-pradesh/noida"],
  ["Nagpur", "maharashtra/nagpur"],
  ["Gurugram", "haryana/gurugram"],
];

/** Cities with the most Investment Advisers on SEBI's register (footer). */
export const IA_TOP_CITIES: [string, string][] = [
  ["Mumbai", "maharashtra/mumbai"],
  ["Bengaluru", "karnataka/bangalore"],
  ["Pune", "maharashtra/pune"],
  ["Chennai", "tamil-nadu/chennai"],
];

/** Well-known NIFTY 50 shares, for footer links when the live list is missing. */
export const TOP_STOCKS: [string, string][] = [
  ["RELIANCE", "Reliance Industries"],
  ["HDFCBANK", "HDFC Bank"],
  ["TCS", "TCS"],
  ["INFY", "Infosys"],
  ["ICICIBANK", "ICICI Bank"],
  ["SBIN", "SBI"],
  ["BHARTIARTL", "Bharti Airtel"],
  ["ITC", "ITC"],
  ["LT", "Larsen & Toubro"],
  ["MARUTI", "Maruti Suzuki"],
];
